import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { useCurrencyMode } from "../lib/useCurrencyMode";
import { useRefresh } from "../contexts/RefreshContext";
import PoDetailModal from "../components/PoDetailModal";
import { EditedPill, latestLineEdit } from "../components/EditedPill";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { SpreadsheetModal } from "../components/SpreadsheetModal";
import { xlsxDate } from "../lib/exportXlsx";
import { WmPart, getWmPartMapSync, wmPartNo } from "../lib/wmParts";
import { MagnifyingGlass, X, Table } from "@phosphor-icons/react";

// Age = days since PO/customer-PO date. Due = days from today to earliest open-line due date.
const AGE_BUCKETS = {
  "0-30": { min: 0, max: 30 },
  "31-60": { min: 31, max: 60 },
  "61-90": { min: 61, max: 90 },
  "90+": { min: 91, max: null },
};
const DUE_BUCKETS = {
  "overdue": { min: null, max: -1 },
  "0-30": { min: 0, max: 30 },
  "31-60": { min: 31, max: 60 },
  "61-90": { min: 61, max: 90 },
  "90+": { min: 91, max: null },
};
const PO_LIMIT = 5000;

// Ex-factory Target Date = delivery/due date − the customer's ex-factory offset (days).
// One shared definition used by the PO header, the line drill-down and the XLSX export so
// the "Target" shown at every level is consistent. Returns an ISO string or null.
const exFactoryTarget = (delivIso, offsetDays) => {
  if (!delivIso) return null;
  const d = new Date(delivIso);
  if (isNaN(d.getTime())) return null;   // guard against unparseable dates
  const off = Number(offsetDays) || 0;
  if (!off) return delivIso;
  d.setDate(d.getDate() - off);
  return d.toISOString();
};

// Cache the last fetched list + scroll position so returning to this screen (e.g. via the
// browser back button after opening a PO) restores the exact list and scroll position
// instead of re-fetching and jumping to the top. Module-level so it survives unmount.
const poListCache = { key: null, data: null, scrollY: 0 };

// The intake list (confirmed/pushed POs merged into this grid) is filter-independent, so cache it
// module-level too — a revisit hydrates instantly and the grid paints in ONE pass instead of a
// second wave when the intake fetch lands after the main list.
const intakeCache = { data: null };

// One lowercased search haystack per PO row — PO#, customer PO, end-cust PO, customer name,
// code, and every line's item code / description / Walmart part#. Built once per row (memoized
// over the loaded list) so client-side search stays instant even near the PO_LIMIT (5000) cap.
const poHaystack = (p, wmMap) => {
  const parts = [p.ms_custpo, p.customer_name, p.ms_pcode, p.ms_pono, p.cust_po2, p.po_number];
  for (const l of (p.lines || [])) {
    parts.push(l.icode, l.name);
    const wp = wmPartNo(wmMap, l.icode);
    if (wp) parts.push(wp);
  }
  return parts.map((x) => String(x ?? "").toLowerCase()).join(" ");
};

export default function MsPOs() {
  const [params, setParams] = useSearchParams();
  const [pos, setPos] = useState([]);
  const [searchPos, setSearchPos] = useState([]);
  const [intakePos, setIntakePos] = useState(intakeCache.data || []);
  // Server-side intake search results — the default intake load only pulls the 500 most-recent
  // POs, so an OLDER intake-only PO would be unfindable by the client-side search. When a term is
  // active we ALSO hit /po?q= to guarantee such POs surface. Merged (deduped by id) into intake.
  const [searchIntakePos, setSearchIntakePos] = useState([]);
  const [plants, setPlants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  // First-paint gates: hold the grid until BOTH the primary list and the (separately fetched)
  // intake list are ready, so rows never appear in waves. Seed from cache for an instant paint.
  const [posReady, setPosReady] = useState(!!poListCache.data);
  const [intakeReady, setIntakeReady] = useState(!!intakeCache.data);
  const mode = useCurrencyMode();
  const { tick } = useRefresh();

  const plant = params.get("plant") || "";
  const customer = params.get("customer") || "";
  // The dropdown option values are ms_pcode; a deep-link may carry the customer NAME instead.
  // Resolve to the matching pcode so the dropdown reflects the active filter either way.
  const customerSel = useMemo(() => {
    if (!customer) return "";
    const m = customers.find((c) => c.ms_pcode === customer || c.name === customer);
    return m?.ms_pcode || "";
  }, [customer, customers]);
  const status = params.has("status") ? params.get("status") : "open";
  const q = params.get("q") || "";
  const age = params.get("age") || "";
  const due = params.get("due") || "";
  const [searchInput, setSearchInput] = useState(q);
  const [groupByCustPo, setGroupByCustPo] = useState(false);
  const [modalPo, setModalPo] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Sync the search box → URL `q` (debounced) so a search is shareable/preserved on reload &
  // back-navigation. Search itself is applied CLIENT-SIDE (below) — this no longer refetches.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get("q") || "") !== searchInput) setParam("q", searchInput);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // When a search term is active, ALSO pull matching POs of ANY status from the server. The
  // main grid loads only the selected status (default "open"), so a fully-shipped/closed
  // release of a searched PO (e.g. OP151657 has an open PoNo 9000290 AND a closed PoNo 9000292)
  // would otherwise stay hidden. Server `q` narrows to a handful of docs, so the live overlay
  // is fast; results are merged (deduped) into the grid below so ALL releases of the PO show.
  useEffect(() => {
    const term = searchInput.trim();
    if (!term) { setSearchPos([]); return; }
    const t = setTimeout(() => {
      const qs = new URLSearchParams();
      qs.set("q", term);
      if (plant) qs.set("plant", plant);
      if (customer) qs.set("customer", customer);
      qs.set("limit", "500");
      api.get(`/ms/pos?${qs.toString()}`)
        .then((r) => setSearchPos(r.data || []))
        .catch(() => setSearchPos([]));
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, plant, customer]);

  // Server-side INTAKE search: the default /po load caps at the 500 most-recent intake POs, so an
  // older intake-only PO (one that was never mirrored as an ERP row) can't be found by the instant
  // client-side search. Hitting /po with the same word-token `q` matches it regardless of recency;
  // results merge into the intake rows deduped by doc id (see allIntakePos) so nothing shows twice.
  useEffect(() => {
    const term = searchInput.trim();
    if (!term) { setSearchIntakePos([]); return; }
    const t = setTimeout(() => {
      api.get("/po", { params: { q: term, status: "confirmed,pushed_to_erp,partially_shipped,fully_shipped" } })
        .then((r) => setSearchIntakePos(r.data || []))
        .catch(() => setSearchIntakePos([]));
    }, 450);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    api.get("/ms/plants").then((r) => setPlants(r.data)).catch(() => {});
    api.get("/ms/customers").then((r) => setCustomers(r.data)).catch(() => {});
    // Intake POs that represent a real order (confirmed or pushed to the ERP, incl. shipped)
    // are merged into this grid, tagged "Intake". Pending-review / superseded stay in the
    // Intake queue only. Broadened from confirmed-only so pushed POs are searchable here too.
    api.get("/po", { params: { status: "confirmed,pushed_to_erp,partially_shipped,fully_shipped" } })
      .then((r) => { const d = r.data || []; setIntakePos(d); intakeCache.data = d; })
      .catch(() => {})
      .finally(() => setIntakeReady(true));
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    if (customer) qs.set("customer", customer);
    if (status) qs.set("status", status);
    if (mode) qs.set("mode", mode);
    const ar = AGE_BUCKETS[age];
    if (ar) { if (ar.min != null) qs.set("age_min", ar.min); if (ar.max != null) qs.set("age_max", ar.max); }
    const dr = DUE_BUCKETS[due];
    if (dr) { if (dr.min != null) qs.set("due_min", dr.min); if (dr.max != null) qs.set("due_max", dr.max); }
    qs.set("limit", String(PO_LIMIT));
    if (tick) qs.set("_t", String(tick));   // manual refresh → new key bypasses client cache; api adds ?refresh=1
    const key = qs.toString();
    // restore scroll for an instant paint, THEN refetch in the background so data changed
    // elsewhere (re-import, edits) is never shown stale.
    if (poListCache.key === key && poListCache.data) {
      setPos(poListCache.data);
      setLoading(false);
      setPosReady(true);
      const y = poListCache.scrollY || 0;
      if (y > 0) {
        // The large table lays out across several frames, so a one-shot scrollTo gets
        // clamped. Retry until we actually reach the target (or give up after ~1.6s).
        let tries = 0;
        const restore = () => {
          window.scrollTo(0, y);
          if (Math.abs(window.scrollY - y) > 2 && tries++ < 40) setTimeout(restore, 40);
        };
        requestAnimationFrame(restore);
      }
      api.get(`/ms/pos?${key}`)
        .then((r) => { setPos(r.data); poListCache.data = r.data; })
        .catch(() => {});
      return;
    }
    setLoading(true);
    api.get(`/ms/pos?${key}`)
      .then((r) => { setPos(r.data); poListCache.key = key; poListCache.data = r.data; poListCache.scrollY = 0; })
      .finally(() => { setLoading(false); setPosReady(true); });
  }, [plant, customer, status, mode, age, due, tick]);

  // Remember scroll position when leaving the screen so back-navigation lands where we left.
  useEffect(() => {
    const save = () => { poListCache.scrollY = window.scrollY; };
    window.addEventListener("scroll", save, { passive: true });
    return () => { save(); window.removeEventListener("scroll", save); };
  }, []);

  // Refetch the current /ms/pos query immediately (used after an inline edit in the PO modal
  // so the grid reflects the new due date / qty / price without waiting for a navigation).
  const reloadPos = useCallback(async () => {
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    if (customer) qs.set("customer", customer);
    if (status) qs.set("status", status);
    if (mode) qs.set("mode", mode);
    const ar = AGE_BUCKETS[age];
    if (ar) { if (ar.min != null) qs.set("age_min", ar.min); if (ar.max != null) qs.set("age_max", ar.max); }
    const dr = DUE_BUCKETS[due];
    if (dr) { if (dr.min != null) qs.set("due_min", dr.min); if (dr.max != null) qs.set("due_max", dr.max); }
    qs.set("limit", String(PO_LIMIT));
    qs.set("refresh", "1");   // post-edit refetch must bypass the 90s grid cache (show my change now)
    const key = qs.toString();
    try {
      const r = await api.get(`/ms/pos?${key}`);
      setPos(r.data);
      if (poListCache.key === key) poListCache.data = r.data;
    } catch { /* keep current grid on transient error */ }
  }, [plant, customer, status, mode, age, due]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (k === "status") np.set(k, v);
    else if (v) np.set(k, v);
    else np.delete(k);
    setParams(np);
  };

  const curOf = (p) => p.currency_code || "INR";
  // Representative unit price for a PO row: single distinct line price, else first (flagged mixed).
  const unitPrice = (p) => {
    const prices = [...new Set((p.lines || []).map((l) => Number(l.price) || 0).filter((v) => v > 0))];
    if (prices.length === 0) return null;
    return { v: prices[0], mixed: prices.length > 1 };
  };

  // Collapse line-level due dates to one PO-row value: the EARLIEST due date among the
  // PO's still-open lines (qty_open > 0) — i.e. the soonest thing still owed ("next due").
  // Falls back to the earliest due across all lines if nothing open carries a date.
  const dueInfo = (p) => {
    const lines = p.lines || [];
    const open = lines.filter((l) => (l.qty_open || 0) > 0 && l.deliv_date).map((l) => l.deliv_date);
    const src = open.length ? open : lines.filter((l) => l.deliv_date).map((l) => l.deliv_date);
    const sorted = [...new Set(src)].sort();
    return { next: sorted[0] || null, count: sorted.length, all: sorted, hasOpen: open.length > 0 };
  };

  // Target Date = customer due date − per-customer ex-factory offset (days). Uses the same
  // "next due" date as the Due Date column. When no offset is configured, target = due.
  const targetDate = (p) => exFactoryTarget(dueInfo(p).next, p.offset_days);

  // Map confirmed intake POs (Mongo) into the SAP grid's shape so both live in one table.
  // Combine the default intake load with any server-side search hits, deduped by doc id, so an
  // older intake-only PO surfaces on search without ever appearing twice.
  const allIntakePos = useMemo(() => {
    if (!searchIntakePos.length) return intakePos;
    const seen = new Set(intakePos.map((p) => String(p.id)));
    const extra = searchIntakePos.filter((p) => !seen.has(String(p.id)));
    return extra.length ? [...intakePos, ...extra] : intakePos;
  }, [intakePos, searchIntakePos]);
  const intakeAsRows = allIntakePos.map((p) => {
    const li = p.line_items || [];
    const lines = li.map((l) => {
      const ordered = Number(l.quantity_ordered) || 0;
      const shipped = Number(l.quantity_shipped) || 0;
      return {
        icode: l.sku || "",
        name: l.description || "",
        qty_ordered: ordered,
        qty_shipped: shipped,
        qty_open: Math.max(0, ordered - shipped),
        price: l.unit_price != null ? Number(l.unit_price) : null,
        deliv_date: l.due_date || null,
        dis_date: null,
      };
    });
    const ord = li.reduce((s, l) => s + (Number(l.quantity_ordered) || 0), 0);
    const shp = li.reduce((s, l) => s + (Number(l.quantity_shipped) || 0), 0);
    return {
      _source: "intake",
      id: p.id,
      ms_pono: p.po_number,
      ms_custpo: p.customer_po || p.po_number,
      cust_po2: p.cust_po2 || "",
      customer_name: p.customer_name,
      ms_pcode: p.resolved_pcode || "—",
      offset_days: p.offset_days || 0,
      po_date: p.order_date,
      lines,
      qty_ordered_total: ord,
      qty_shipped_total: shp,
      qty_pending_total: Math.max(0, ord - shp),
      amount: p.total_amount || 0,
      currency_code: p.currency || "USD",
      status: p.status,
      rev_no: p.is_change_order ? 1 : 0,
    };
  });
  // Instant client-side search over the ALREADY-LOADED rows (PO#, customer PO, end-cust PO,
  // customer name, code, and each line's item code / description / Walmart part#). This mirrors
  // the fields the old server `q` matched, but runs in the browser so typing is instant — the
  // /ms/pos endpoint re-runs a slow live-ERP allocation overlay (11–42s) on every call, which is
  // what made server-side search feel frozen. Plant/customer/status/age/due stay server-side.
  const searchWords = searchInput.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const wmMapSync = getWmPartMapSync();
  const matchHay = (hay) => searchWords.every((w) => hay.includes(w));
  const rowMatchesSearch = (p) => !searchWords.length || matchHay(poHaystack(p, wmMapSync));

  // The selected Status filter is AUTHORITATIVE — it must win even over the cross-status search
  // merge below. Without this a broad search (e.g. an item description that also appears on 210
  // CLOSED POs) floods the grid with closed rows while "Open" is selected. "" = All statuses.
  // Mirrors the server's /ms/pos semantics: status=open also includes partially_shipped; intake
  // rows carry confirmed/pushed/fully_shipped states that map onto open/closed.
  const statusMatch = (p) => {
    if (!status) return true;
    const st = p.status;
    if (status === "open") return ["open", "partially_shipped", "confirmed", "pushed_to_erp"].includes(st);
    if (status === "closed") return ["closed", "fully_shipped"].includes(st);
    return st === status;
  };

  // Merge the default (status-filtered) load with the search results so a searched PO shows every
  // release that ALSO matches the current status filter. Cross-status discovery is available by
  // switching Status to "All". Deduped by internal PO id / PoNo. No search → identical to `pos`.
  const mergedPos = useMemo(() => {
    if (!searchPos.length) return pos;
    const seen = new Set(pos.map((p) => String(p.id ?? p.ms_pono)));
    const extra = searchPos.filter((p) => !seen.has(String(p.id ?? p.ms_pono)) && statusMatch(p));
    return extra.length ? [...pos, ...extra] : pos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, searchPos, status]);


  // Dedup: a pushed intake PO can also be materialized as a "via Intake" ERP row in /ms/pos
  // (it carries intake_po_id / source_file="INTAKE push"). Prefer that ERP row (it has live
  // shipped qty) and drop the duplicate intake row so it doesn't appear twice.
  const intakeIdsInSap = new Set(mergedPos.map((p) => p.intake_po_id).filter(Boolean).map(String));
  const viaIntakePoNums = new Set(
    mergedPos.filter((p) => p.source_file === "INTAKE push" || p.intake_po_id)
      .flatMap((p) => [p.ms_custpo, p.ms_pono].filter(Boolean))
      .map((x) => String(x).trim().toLowerCase())
  );
  // Honor the shared filters: intake POs carry no SAP plant/customer, so hide them when
  // those SAP-only filters are active; apply status(closed) and search text.
  const filteredIntake = intakeAsRows.filter((p) => {
    if (intakeIdsInSap.has(String(p.id))) return false;
    const num = String(p.ms_pono || "").trim().toLowerCase();
    const cnum = String(p.ms_custpo || "").trim().toLowerCase();
    if ((num && viaIntakePoNums.has(num)) || (cnum && viaIntakePoNums.has(cnum))) return false;
    if (plant || customer) return false;
    if (!statusMatch(p)) return false;
    if (!rowMatchesSearch(p)) return false;
    return true;
  });
  // Group internal-PO rows under their shared Customer PO. Customer POs with a single
  // internal PO stay as normal (non-grouped) rows. Intake rows are never grouped.
  const groupByCust = (flat) => {
    const map = new Map();
    for (const p of flat) {
      const key = p.ms_custpo || p.ms_pono;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    const out = [];
    for (const [key, members] of map) {
      if (members.length === 1) { out.push(members[0]); continue; }
      const sum = (f) => members.reduce((s, m) => s + (Number(f(m)) || 0), 0);
      const allLines = members.flatMap((m) => (m.lines || []).map((l) => ({ ...l, _pono: m.ms_pono })));
      const src0 = members[0].source;
      out.push({
        _group: true, _children: members, _key: key,
        ms_custpo: key, ms_pono: null,
        cust_po2: members.map((m) => m.cust_po2).find(Boolean) || "",
        customer_name: members[0].customer_name, ms_pcode: members[0].ms_pcode,
        source: members.every((m) => m.source === src0) ? src0 : "mixed",
        _via_intake: members.some((m) => m.source_file === "INTAKE push" || m.intake_po_id),
        po_date: members.map((m) => m.po_date).filter(Boolean).sort()[0] || null,
        lines: allLines, offset_days: members[0].offset_days,
        qty_ordered_total: sum((m) => m.qty_ordered_total),
        qty_shipped_total: sum((m) => m.qty_shipped_total),
        qty_pending_total: sum((m) => m.qty_pending_total),
        amount: sum((m) => m.amount),
        currency: members[0].currency, currency_code: members[0].currency_code,
        status: members.some((m) => (m.qty_pending_total || 0) > 0) ? "open" : "closed",
        rev_no: Math.max(...members.map((m) => m.rev_no || 0)),
      });
    }
    return out;
  };
  // Pre-compute the search haystack for every loaded SAP row ONCE per fetch (not per keystroke)
  // so search stays instant even near the 5000-row cap. Intake rows are few — matched directly.
  const sapHay = useMemo(
    () => mergedPos.map((p) => ({ row: { ...p, _source: "sap" }, hay: poHaystack(p, wmMapSync) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedPos]
  );
  const sapRows = (searchWords.length ? sapHay.filter((x) => matchHay(x.hay)) : sapHay).map((x) => x.row);
  const rows = groupByCustPo
    ? [...groupByCust(sapRows), ...filteredIntake]
    : [...sapRows, ...filteredIntake];
  // Hold the first paint until both data sources are ready so rows don't stream in in waves.
  const booting = !posReady || !intakeReady;

  const columns = [
    { key: "_source", header: "Source",
      render: (p) => {
        // Reflect the real data origin: Intake (review queue) vs Excel import vs ERP sync.
        let label, style, tid;
        // A pushed intake PO is materialized as source='erp' but keeps its intake provenance
        // (source_file="INTAKE push" / intake_po_id) — surface that so it isn't confusing.
        const viaIntake = p.source_file === "INTAKE push" || !!p.intake_po_id || !!p._via_intake;
        if (p._source === "intake") {
          label = "Intake"; tid = "intake";
          style = { background: "#EDE9FE", color: "#5B21B6", borderColor: "#C4B5FD" };
        } else if (p.source === "mixed") {
          label = "Mixed"; tid = "mixed";
          style = { background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A" };
        } else if (p.source === "excel") {
          label = "Excel"; tid = "excel";
          style = { background: "#DCFCE7", color: "#166534", borderColor: "#86EFAC" };
        } else {
          label = "ERP Sync"; tid = "erp";
          style = { background: "#E0F2FE", color: "#075985", borderColor: "#7DD3FC" };
        }
        const showVia = viaIntake && p._source !== "intake";
        return (
          <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
            <span className="jde-badge" data-testid={`src-${tid}`} style={style}
              title={showVia ? `Originated from an intake PO${p.pushed_at ? `, pushed to ERP on ${fmtDate(p.pushed_at)}` : " (pushed to ERP)"}` : undefined}>{label}</span>
            {showVia && (
              <span data-testid="src-via-intake" title="This PO started in the intake queue and was pushed to the ERP"
                style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, fontWeight: 700, color: "#5B21B6", letterSpacing: 0.2 }}>
                · via Intake
              </span>
            )}
          </span>
        );
      } },
    { key: "ms_custpo", header: "Customer PO",
      render: (p) => {
        const le = latestLineEdit(p);
        return (
        <span>
          <strong className="text-mono">{p.ms_custpo || "—"}</strong>
          {p._group && (
            <span className="jde-badge" style={{ marginLeft: 6, background: "#E0E7FF", color: "#3730A3", fontWeight: 700, fontSize: 10 }} data-testid={`po-group-${p._key}`}>
              {p._children.length} rel
            </span>
          )}
          {p.rev_no > 0 && (
            <span className="jde-badge partial" style={{ marginLeft: 6, fontSize: 9 }} title={`Revision ${p.rev_no}`}>R{p.rev_no}</span>
          )}
          {le.edit && (
            <div style={{ marginTop: 4 }}>
              <EditedPill edit={le.edit} count={le.count} testid={`po-row-edited-${p.ms_pono}`} />
            </div>
          )}
        </span>
        );
      } },
    { key: "po_date", header: "PO Date", type: "date", accessor: (p) => p.po_date,
      render: (p) => <span className="text-mono" style={{ fontSize: 11 }}>{fmtDate(p.po_date)}</span> },
    { key: "cust_po2", header: "End Customer PO Number", accessor: (p) => p.cust_po2 || "",
      render: (p) => <span className="text-mono" style={{ fontSize: 11 }}>{p.cust_po2 || <span className="muted">—</span>}</span> },
    { key: "customer_name", header: "Customer",
      render: (p) => <span>{p.customer_name}</span> },
    { key: "due_date", header: "Due Date", type: "date", accessor: (p) => dueInfo(p).next,
      render: (p) => {
        const d = dueInfo(p);
        if (!d.next) return <span className="muted">—</span>;
        const overdue = d.hasOpen && new Date(d.next) < new Date(new Date().toDateString());
        return (
          <span className="text-mono" style={{ fontSize: 11, color: overdue ? "#B91C1C" : "inherit", fontWeight: overdue ? 700 : 400 }}
                title={d.count > 1 ? `Open-line due dates:\n${d.all.map(fmtDate).join("\n")}` : ""}
                data-testid={`due-${p._key || p.ms_pono}`}>
            {fmtDate(d.next)}
            {d.count > 1 && <span className="muted" style={{ marginLeft: 4, fontSize: 10 }}>+{d.count - 1}</span>}
          </span>
        );
      } },
    { key: "target_date", header: "Target Date", type: "date", accessor: (p) => targetDate(p),
      render: (p) => {
        const t = targetDate(p);
        if (!t) return <span className="muted">—</span>;
        const off = Number(p.offset_days) || 0;
        const overdue = new Date(t) < new Date(new Date().toDateString()) && (p.qty_pending_total || 0) > 0;
        return (
          <span className="text-mono" style={{ fontSize: 11, fontWeight: 600, color: overdue ? "#B91C1C" : "#B45309" }}
                title={off ? `Customer due − ${off}d ex-factory offset` : "No offset configured (= due date)"}
                data-testid={`target-${p._key || p.ms_pono}`}>
            {fmtDate(t)}
            {off > 0 && <span className="muted" style={{ marginLeft: 4, fontSize: 10 }}>(−{off}d)</span>}
          </span>
        );
      } },
    { key: "cfs_cutoff_date", header: "CFS Cut-Off", type: "date", accessor: (p) => p.cfs_cutoff_date || null,
      render: (p) => p.cfs_cutoff_date
        ? <span className="text-mono" style={{ fontSize: 11, fontWeight: 700, color: "#B45309", background: "#fff7ed", padding: "2px 7px", borderRadius: 6, border: "1px solid #fed7aa" }}
                 title="CFS cut-off (logistics) — edit on the CFS Cut-Off page" data-testid={`cfs-${p._key || p.ms_pono}`}>{fmtDate(p.cfs_cutoff_date)}</span>
        : <span className="muted">—</span> },
    { key: "must_ship_by", header: "Must Ship By", type: "date", accessor: (p) => p.must_ship_by || null,
      render: (p) => p.must_ship_by
        ? <span className="text-mono" style={{ fontSize: 11, fontWeight: 700, color: "#B91C1C" }}
                 title="Must ship by = CFS cut-off − 2 business days" data-testid={`msb-${p._key || p.ms_pono}`}>{fmtDate(p.must_ship_by)}</span>
        : <span className="muted">—</span> },
    { key: "qty_ordered_total", header: "Qty Ord", align: "right",
      render: (p) => <span className="text-mono">{fmtNum(p.qty_ordered_total)}</span> },
    { key: "qty_shipped_total", header: "Qty Ship", align: "right",
      render: (p) => <span className="text-mono" style={{ color: "#0F766E" }}>{fmtNum(p.qty_shipped_total)}</span> },
    { key: "qty_pending_total", header: "Qty Open", align: "right",
      render: (p) => <span className="text-mono" style={{ fontWeight: 700, color: p.qty_pending_total > 0 ? "#B91C1C" : "#10B981" }}>{fmtNum(p.qty_pending_total)}</span> },
    { key: "unit_price", header: "Unit Price", align: "right", accessor: (p) => (unitPrice(p)?.v ?? 0),
      render: (p) => {
        const up = unitPrice(p);
        if (!up) return <span className="muted">—</span>;
        return <span className="text-mono" title={up.mixed ? "Multiple prices across releases" : ""}>{up.mixed ? "~" : ""}{fmtCur(up.v, curOf(p))}</span>;
      } },
    { key: "amount", header: "Amount", align: "right",
      render: (p) => <span className="text-mono">{fmtCur(p.amount, curOf(p))}</span> },
    { key: "status", header: "Status",
      render: (p) => <span className={`jde-badge ${p.status === "closed" ? "shipped" : p.status === "partially_shipped" ? "partial" : "pending"}`}>{p.status}</span> },
  ];

  const activeFilterCount = [status && status !== "open", plant, customer, age, due].filter(Boolean).length;

  // Item-level export: one row per PO line item (the PO header fields are repeated on every
  // line) so users get a true item-level report instead of PO headers only. POs with no
  // lines still emit a single row so nothing silently disappears from the export.
  const srcLabel = (p) => p._source === "intake" ? "Intake" : p.source === "mixed" ? "Mixed" : p.source === "excel" ? "Excel" : "ERP Sync";
  const exportRows = rows.flatMap((p) => {
    const lines = p.lines || [];
    return (lines.length ? lines : [null]).map((l) => ({ p, l }));
  });
  const exportColumns = [
    { header: "Source", value: ({ p }) => srcLabel(p) },
    { header: "Customer PO", value: ({ p }) => p.ms_custpo || "" },
    { header: "PO #", value: ({ p }) => p.ms_pono || "" },
    { header: "PO Date", value: ({ p }) => xlsxDate(p.po_date) },
    { header: "Customer", value: ({ p }) => p.customer_name || "" },
    { header: "Item #", value: ({ l }) => (l && l.icode) || "" },
    { header: "WM Part #", value: ({ l }) => (l ? wmPartNo(getWmPartMapSync(), l.icode) : "") },
    { header: "Description", value: ({ l }) => (l && l.name) || "" },
    { header: "Qty Ordered", value: ({ l }) => (l ? Number(l.qty_ordered) || 0 : ""), numeric: true },
    { header: "Qty Shipped", value: ({ l }) => (l ? Number(l.qty_shipped) || 0 : ""), numeric: true },
    { header: "Qty Open", value: ({ l }) => (l ? Number(l.qty_open) || 0 : ""), numeric: true },
    { header: "Unit Price", value: ({ l }) => (l && l.price != null ? Number(l.price) : ""), numeric: true },
    { header: "Line Amount", value: ({ l }) => (l ? Number(l.amt) || (Number(l.price) || 0) * (Number(l.qty_ordered) || 0) : ""), numeric: true },
    { header: "Currency", value: ({ p }) => curOf(p) },
    { header: "Due Date", value: ({ l }) => (l ? xlsxDate(l.deliv_date) : "") },
    { header: "Target Date", value: ({ p, l }) => (l ? xlsxDate(exFactoryTarget(l.deliv_date, p && p.offset_days)) : "") },
    { header: "Ship Date", value: ({ l }) => (l && (Number(l.qty_shipped) || 0) > 0 ? xlsxDate(l.dis_date) : "") },
    { header: "Line Status", value: ({ l }) => (l ? lineStatus(l).label : "") },
    { header: "PO Status", value: ({ p }) => p.status || "" },
  ];

  const clearFilters = () => {
    const np = new URLSearchParams(params);
    ["plant", "customer", "age", "due"].forEach((k) => np.delete(k));
    np.set("status", "open");
    setParams(np);
  };
  const toolbar = (
    <div className="po-filterbar" data-testid="po-filterbar">
      <div className="po-filter-search">
        <MagnifyingGlass size={13} weight="bold" style={{ color: "var(--omh-text-muted)", flex: "0 0 auto" }} />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search PO #, customer PO, part # or description…"
          data-testid="po-server-search"
        />
      </div>
      <select className="jde-select" value={status} onChange={(e) => setParam("status", e.target.value)} data-testid="filter-status" title="Status">
        <option value="">Status · All</option>
        <option value="open">Open</option>
        <option value="partially_shipped">Partially shipped</option>
        <option value="closed">Closed</option>
      </select>
      <select className="jde-select" value={plant} onChange={(e) => setParam("plant", e.target.value)} data-testid="filter-plant" title="Plant">
        <option value="">All plants</option>
        {plants.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select className="jde-select" value={customerSel} onChange={(e) => setParam("customer", e.target.value)} data-testid="filter-customer" title="Customer">
        <option value="">All customers</option>
        {customers.map((c, i) => <option key={`${c.ms_pcode || c.name}-${i}`} value={c.ms_pcode || ""}>{`${c.name}${c.ms_pcode ? ` (${c.ms_pcode})` : ""}`}</option>)}
      </select>
      <select className="jde-select" value={age} onChange={(e) => setParam("age", e.target.value)} data-testid="filter-age" title="PO age">
        <option value="">All ages</option>
        <option value="0-30">Age 0–30d</option>
        <option value="31-60">Age 31–60d</option>
        <option value="61-90">Age 61–90d</option>
        <option value="90+">Age 90+d</option>
      </select>
      <select className="jde-select" value={due} onChange={(e) => setParam("due", e.target.value)} data-testid="filter-due" title="Due date">
        <option value="">All due dates</option>
        <option value="overdue">Overdue</option>
        <option value="0-30">Due 0–30d</option>
        <option value="31-60">Due 31–60d</option>
        <option value="61-90">Due 61–90d</option>
        <option value="90+">Due 90+d</option>
      </select>
      {activeFilterCount > 0 && (
        <button type="button" className="po-filter-clear" onClick={clearFilters} data-testid="po-filter-clear" title="Clear filters">
          <X size={11} weight="bold" /> Clear ({activeFilterCount})
        </button>
      )}
    </div>
  );

  // Bottom summary row — shown whenever a filter/search is active, aggregating the
  // currently displayed POs (qty totals + amount grouped by currency).
  const summaryBar = (shown, all) => {
    const clientFiltered = shown.length !== all.length;
    const active = !!(searchInput.trim() || customer || plant || age || due) || clientFiltered;
    if (!active || shown.length === 0) return null;
    const sum = (f) => shown.reduce((s, p) => s + (Number(f(p)) || 0), 0);
    const ord = sum((p) => p.qty_ordered_total);
    const ship = sum((p) => p.qty_shipped_total);
    const open = sum((p) => p.qty_pending_total);
    // "Amount" = full ordered PO value (qty ordered × price, incl. already-shipped).
    // "Open value" = unshipped backlog only (Σ line qty_open × price) — reconciles with the
    // Dashboard's per-customer Value. Both grouped by the PO's currency.
    const byCur = {};
    const byCurOpen = {};
    shown.forEach((p) => {
      const c = curOf(p);
      byCur[c] = (byCur[c] || 0) + (Number(p.amount) || 0);
      const ov = (p.lines || []).reduce(
        (s, l) => s + (Number(l.qty_open) || 0) * (Number(l.price) || 0), 0);
      byCurOpen[c] = (byCurOpen[c] || 0) + ov;
    });
    return (
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center", fontSize: 12 }} data-testid="po-summary-bar">
        <span style={{ fontWeight: 700 }}>Filtered totals · {fmtNum(shown.length)} POs</span>
        <span>Qty Ord: <strong className="text-mono">{fmtNum(ord)}</strong></span>
        <span>Qty Ship: <strong className="text-mono" style={{ color: "#0F766E" }}>{fmtNum(ship)}</strong></span>
        <span>Qty Open: <strong className="text-mono" style={{ color: "#B91C1C" }}>{fmtNum(open)}</strong></span>
        {Object.entries(byCur).map(([c, v]) => (
          <span key={c} title="Full ordered value (qty ordered × price, includes shipped)">
            Amount ({c}): <strong className="text-mono">{fmtCur(v, c)}</strong>
          </span>
        ))}
        {Object.entries(byCurOpen).map(([c, v]) => (
          <span key={`open-${c}`} data-testid={`po-summary-open-value-${c}`}
                title="Open backlog value (qty open × price) — matches the Dashboard customer Value">
            Open value ({c}): <strong className="text-mono" style={{ color: "#B91C1C" }}>{fmtCur(v, c)}</strong>
          </span>
        ))}
      </div>
    );
  };

  return (
    <Layout breadcrumb={["Home", "Purchase Orders"]}>
      <div className="jde-page">
        <div className="jde-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title">PURCHASE ORDERS ({rows.length}{loading ? "…" : ""})</div>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "var(--omh-text-muted)" }} data-testid="po-group-toggle">
              <input type="checkbox" checked={groupByCustPo} onChange={(e) => setGroupByCustPo(e.target.checked)} data-testid="po-group-checkbox" />
              Group by Customer PO
            </label>
            <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={() => setSheetOpen(true)} data-testid="open-spreadsheet"
              title="Open the currently filtered PO line items in a spreadsheet — drag-select cells to total, analyze, add formulas"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Table size={12} weight="bold" /> Open in Spreadsheet
            </button>
            <ExportXlsxButton filename="purchase-orders-items" columns={exportColumns} rows={exportRows} sheetName="PO Line Items" testid="po-export-xlsx" />
            {pos.length >= PO_LIMIT && (
              <span className="muted" style={{ fontSize: 11, color: "#B45309", marginLeft: 12 }} data-testid="po-truncation-note">
                Showing first {PO_LIMIT.toLocaleString()} — narrow with filters or search to see the rest.
              </span>
            )}
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <style>{`
              .po-grid-compact .jde-grid { font-size: 11.5px; }
              .po-grid-compact .jde-grid th, .po-grid-compact .jde-grid td { padding: 5px 8px; }
              .po-grid-compact .jde-grid .jde-badge { font-size: 9.5px; padding: 1px 5px; }
              /* Compact the shared SortableGrid toolbar (quick filter + date range) */
              .po-grid-compact .jde-input, .po-grid-compact .jde-select { height: 30px; font-size: 12px; padding: 4px 10px; }
              .po-grid-compact .jde-select { padding-right: 26px; background-position: right 8px center; }
              @keyframes poShimmer { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
              /* Inline filter bar */
              .po-filterbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; flex: 1 1 100%; }
              .po-filterbar .jde-select { flex: 0 1 auto; width: auto; min-width: 118px; max-width: 190px; }
              .po-filter-search { display: flex; align-items: center; gap: 6px; flex: 1 1 240px; min-width: 200px;
                background: #fff; border: 1px solid var(--omh-border-strong); border-radius: var(--omh-radius-sm);
                height: 30px; padding: 0 10px; transition: border-color 0.15s, box-shadow 0.15s; }
              .po-filter-search:focus-within { border-color: var(--omh-primary); box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
              .po-filter-search input { border: 0; outline: none; background: transparent; width: 100%; font-size: 12px; color: var(--omh-text); font-family: inherit; }
              .po-filter-clear { display: inline-flex; align-items: center; gap: 4px; height: 30px; padding: 0 10px;
                border: 1px solid var(--omh-border-strong); background: #fff; border-radius: var(--omh-radius-sm);
                font-size: 12px; font-weight: 600; color: var(--omh-text-muted); cursor: pointer; white-space: nowrap; transition: all 0.15s ease; }
              .po-filter-clear:hover { background: var(--omh-bg-subtle); color: var(--omh-alert); border-color: #94A3B8; }
            `}</style>
            {!loading && searchInput.trim() && status && rows.length === 0 && pos.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                            background: "#FEF3C7", borderBottom: "1px solid #FDE68A", fontSize: 12.5, color: "#92400E" }}
                   data-testid="po-search-status-hint">
                <span>No <strong>{status.replace("_", " ")}</strong> POs match “{searchInput}”. It may be on POs with another status.</span>
                <button className="jde-btn jde-btn-sm" onClick={() => setParam("status", "")} data-testid="po-search-all-statuses">
                  Search all statuses →
                </button>
              </div>
            )}
            {booting ? (
              <div data-testid="po-loading-skeleton" style={{ padding: "10px 12px" }}>
                {Array.from({ length: 14 }).map((_, i) => (
                  <div key={i} style={{
                    height: 30, borderRadius: 6, margin: "7px 0", opacity: 1 - i * 0.045,
                    background: "linear-gradient(90deg,#EEF2F6 25%,#E2E8F0 37%,#EEF2F6 63%)",
                    backgroundSize: "200% 100%", animation: "poShimmer 1.1s ease-in-out infinite",
                  }} />
                ))}
              </div>
            ) : (
            <div className="po-grid-compact">
            <SortableGrid
              columns={columns}
              rows={rows}
              pageSize={100}
              getRowKey={(p) => (p._group ? `g-${p._key}` : p._source === "intake" ? `i-${p.id}` : p.ms_pono)}
              renderExpanded={(p) => (p._group ? <GroupChildren group={p} onView={setModalPo} /> : <PoLinesDetail po={p} onView={setModalPo} />)}
              defaultSort={{ key: "po_date", dir: "desc" }}
              searchPlaceholder="Quick filter loaded rows…"
              testid="po-table"
              toolbarRight={toolbar}
              statusBar={summaryBar}
              emptyMessage="No POs match these filters."
            />
            </div>
            )}
          </div>
        </div>
      </div>
      <PoDetailModal po={modalPo} onClose={() => setModalPo(null)} onChanged={reloadPos} />
      {sheetOpen && (
        <SpreadsheetModal
          title="Purchase Orders"
          columns={exportColumns}
          rows={exportRows}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </Layout>
  );
}

const lineStatus = (l) => {
  const open = Number(l.qty_open) || 0;
  const ship = Number(l.qty_shipped) || 0;
  if (open <= 0) return { label: "Fulfilled", bg: "#DCFCE7", fg: "#166534" };
  if (ship > 0) return { label: "Partially shipped", bg: "#FEF3C7", fg: "#92400E" };
  return { label: "Open", bg: "#DBEAFE", fg: "#1E40AF" };
};

function PoLinesDetail({ po, onView }) {
  const navigate = useNavigate();
  const lines = po.lines || [];
  const th = { textAlign: "left", padding: "5px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap" };
  const td = { padding: "5px 8px", fontSize: 11.5, borderBottom: "1px solid #EEF1F4" };
  const cur = po.currency || "USD";
  return (
    <div style={{ padding: "10px 16px 14px" }} data-testid={`po-lines-${po.ms_pono || po.id}`}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#475569" }}>
          Line items · {po.ms_custpo || po.ms_pono} <span className="muted" style={{ fontWeight: 500 }}>({lines.length})</span>
        </span>
        <span style={{ flex: 1 }} />
        {po._source === "intake" ? (
          <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/po/${po.id}`); }} data-testid={`po-view-intake-${po.id}`}>
            View / Edit →
          </button>
        ) : (
          <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); onView(po); }} data-testid={`po-view-full-${po.ms_pono}`}>
            View / Edit →
          </button>
        )}
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {["Item", "Description", "Ordered", "Shipped", "Ship Date", "Open", "Unit Price", "Line Status", "Due", "Target"].map((h, i) => (
              <th key={h} style={{ ...th, textAlign: (i >= 2 && i <= 3) || (i >= 5 && i <= 6) ? "right" : "left" }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {lines.map((l, idx) => {
              const st = lineStatus(l);
              return (
                <tr key={`${l.icode}-${idx}`}>
                  <td style={{ ...td, fontWeight: 700 }} className="text-mono">{l.icode || "—"}<WmPart icode={l.icode} /></td>
                  <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.name || ""}>{l.name || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtNum(l.qty_ordered)}</td>
                  <td style={{ ...td, textAlign: "right", color: "#0F766E" }} className="text-mono">{fmtNum(l.qty_shipped)}</td>
                  <td style={{ ...td, color: "#0F766E" }} className="text-mono" data-testid={`po-line-shipdate-${po.ms_pono || po.id}-${idx}`}>
                    {(Number(l.qty_shipped) || 0) > 0 && l.dis_date ? fmtDate(l.dis_date) : "—"}
                    {Array.isArray(l.inv_nos) && l.inv_nos.length > 0 && (
                      <div style={{ marginTop: 3 }}>
                        <span className="jde-badge" title={`Shipped on invoice ${l.inv_nos.join(", ")}`}
                          style={{ background: "#EEF2FF", color: "#3730A3", fontWeight: 700, fontSize: 9, padding: "1px 6px", borderRadius: 8, letterSpacing: 0.2 }}
                          data-testid={`po-line-invno-${po.ms_pono || po.id}-${idx}`}>
                          Inv {l.inv_nos.length === 1 ? l.inv_nos[0] : `${l.inv_nos[0]} +${l.inv_nos.length - 1}`}
                        </span>
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: (Number(l.qty_open) || 0) > 0 ? "#B91C1C" : "#166534" }} className="text-mono">{fmtNum(l.qty_open)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{l.price ? fmtCur(l.price, cur) : "—"}</td>
                  <td style={td}><span className="jde-badge" style={{ background: st.bg, color: st.fg, fontWeight: 700, fontSize: 10 }}>{st.label}</span></td>
                  <td style={td} className="text-mono">{l.deliv_date ? fmtDate(l.deliv_date) : "—"}</td>
                  <td style={td} className="text-mono">
                    {(() => {
                      const t = exFactoryTarget(l.deliv_date, po.offset_days);
                      if (!t) return <span className="muted">—</span>;
                      const off = Number(po.offset_days) || 0;
                      return (
                        <span title={off ? `Customer due − ${off}d ex-factory offset` : "No offset configured (= due date)"}>
                          {fmtDate(t)}
                          {off > 0 && <span className="muted" style={{ marginLeft: 4, fontSize: 10 }}>(−{off}d)</span>}
                        </span>
                      );
                    })()}
                  </td>
                </tr>);
            })}
            {lines.length === 0 && <tr><td colSpan={10} className="muted" style={{ padding: 14, textAlign: "center" }}>No line items.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Drill-down for a Customer-PO group: one row per internal PO (release) that shares the
// same customer PO, showing its item, qtys, delivery date and a link to the full PO.
function GroupChildren({ group, onView }) {
  const th = { textAlign: "left", padding: "5px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap" };
  const td = { padding: "5px 8px", fontSize: 11.5, borderBottom: "1px solid #EEF1F4", whiteSpace: "nowrap" };
  const cur = group.currency_code || group.currency || "USD";
  const children = [...group._children].sort((a, b) => {
    const da = (a.lines?.[0]?.deliv_date) || "", db = (b.lines?.[0]?.deliv_date) || "";
    return String(da).localeCompare(String(db));
  });
  const upOf = (c) => {
    const p = (c.lines || []).map((l) => Number(l.price) || 0).find((v) => v > 0);
    return p || 0;
  };
  // Ship date for a release = the dis_date of its earliest-shipped line (Excel actual dispatch
  // pre-cutover, or gsti-derived post-cutover). Only meaningful when something has shipped.
  const shipDateOf = (c) => {
    const ds = (c.lines || [])
      .filter((l) => (Number(l.qty_shipped) || 0) > 0 && l.dis_date)
      .map((l) => l.dis_date)
      .sort();
    return ds[0] || null;
  };
  return (
    <div style={{ padding: "10px 16px 14px" }} data-testid={`po-group-lines-${group._key}`}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#475569", marginBottom: 6 }}>
        Customer PO {group.ms_custpo} · {children.length} releases
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {["Release", "Item", "Description", "Ordered", "Shipped", "Open", "Unit Price", "Amount", ""].map((h, i) => (
              <th key={i} style={{ ...th, textAlign: i >= 3 && i <= 7 ? "right" : "left" }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {children.map((c, idx) => {
              const l0 = (c.lines && c.lines[0]) || {};
              const dd = l0.deliv_date;
              const up = upOf(c);
              return (
                <tr key={c.ms_pono || idx}>
                  <td style={{ ...td, fontWeight: 700 }}>
                    <span className="jde-badge" style={{ background: "#E0F2FE", color: "#075985", fontSize: 10, fontWeight: 700 }}>R{idx + 1}</span>
                    <span className="text-mono" style={{ marginLeft: 6, fontWeight: 400 }}>{dd ? `due ${fmtDate(dd)}` : "no date"}</span>
                  </td>
                  <td style={td} className="text-mono">{l0.icode || "—"}<WmPart icode={l0.icode} /></td>
                  <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }} title={l0.name || ""}>{l0.name || (c.lines?.length > 1 ? `${c.lines.length} items` : "—")}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtNum(c.qty_ordered_total)}</td>
                  <td style={{ ...td, textAlign: "right", color: "#0F766E" }} className="text-mono">
                    {fmtNum(c.qty_shipped_total)}
                    {shipDateOf(c) && <div style={{ fontSize: 9, color: "#64748B", fontWeight: 500 }} data-testid={`po-group-shipdate-${c.ms_pono || idx}`}>shipped {fmtDate(shipDateOf(c))}</div>}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: (Number(c.qty_pending_total) || 0) > 0 ? "#B91C1C" : "#166534" }} className="text-mono">{fmtNum(c.qty_pending_total)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{up ? fmtCur(up, c.currency_code || cur) : "—"}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtCur(c.amount, c.currency_code || cur)}</td>
                  <td style={td}>
                    <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); onView(c); }} data-testid={`po-view-full-${c.ms_pono}`}>View / Edit →</button>
                  </td>
                </tr>);
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
